import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { BrandKit } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Data {
  brand_kit: BrandKit
}

export interface Variables {
  id: number
  externalIntegrationIds: number[]
  signal?: AbortSignal
}

export const addExternalIntegrationsToBrandKit = async ({
  workspaceId,
  id,
  externalIntegrationIds,
  signal,
}: Variables & { workspaceId: number }) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${id}/add_external_integrations`, {
    method: 'POST',
    signal,
    body: {
      external_integration_ids: externalIntegrationIds.join(','),
    },
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res.data
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    throw new Error(res.data)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<BrandKit, Error, Variables>, 'mutationFn'> {}

export const useAddExternalIntegrationsToBrandKit = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<BrandKit, Error, Variables>(async (vars) => {
    const { id, externalIntegrationIds, signal } = vars

    const result = await addExternalIntegrationsToBrandKit({
      workspaceId,
      id,
      externalIntegrationIds,
      signal,
    })

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', id])

    return result.brand_kit
  }, options)
}
